import { RefObject } from 'react';
import { CreateVisitForm } from '@act/shared/data-transfer-objects';
import { SelectOption } from '@act/storybook';
import { NoteFieldRef, NoteFieldSaveOverride } from 'react-components/dialog-forms/NoteField';
import { visitDurations } from 'redux/constants/durationsData';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';

/** Stable draft id for the member-page "New visit" create dialog. */
export const MANUAL_VISIT_NOTE_DRAFT_ID = 'manual-create';

export interface VisitFormConfigParams {
  dateOptions: SelectOption<string>[];
  startTimeOptions: SelectOption<number>[];
  addressOptions: SelectOption<number>[];
  outcomeOptions: SelectOption<number>[];
  addressId: number;
  canEditEngagement: boolean;
  /** Shown in the note field character count when the outcome requires a minimum length. */
  characterMinimum?: number;
  noteMode: 'create' | 'edit';
  visitId?: number;
  /** Known note id from the visit payload — lets NoteField skip the by-item lookup. */
  noteId?: number;
  userId: string;
  /** Wired by VisitDialog so Save can flush pending note autosave first. */
  noteFieldRef?: RefObject<NoteFieldRef | null>;
  /** Edit-mode autosave via parent PATCH (`updateVisit` with `{ note }`). */
  noteSave?: NoteFieldSaveOverride;
  /** Propagates recovered-note state to the dialog header title. */
  onRecoveredNoteChange?: (isRecovered: boolean) => void;
  /** Passthrough rows for hospitalization follow-up */
  hospitalizationFollowUpFields: FormConfig<CreateVisitForm>;
}

/**
 * Builds the VisitDialog FormBuilder config. Extracted from VisitDialog to keep
 * that component within size limits; the note uses the unified `noteField`.
 */
export function buildVisitFormConfig({
  dateOptions,
  startTimeOptions,
  addressOptions,
  outcomeOptions,
  addressId,
  canEditEngagement,
  characterMinimum,
  noteMode,
  visitId,
  noteId,
  userId,
  noteFieldRef,
  noteSave,
  onRecoveredNoteChange,
  hospitalizationFollowUpFields = [],
}: VisitFormConfigParams): FormConfig<CreateVisitForm> {
  return [
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'date',
          label: 'Date',
          input: 'select',
          disabled: !canEditEngagement,
          props: {
            options: dateOptions,
            trackingName: 'visit-date-select',
          },
        },
        {
          type: 'input',
          name: 'start',
          label: 'Start Time',
          input: 'select',
          size: 1,
          disabled: !canEditEngagement,
          props: {
            options: startTimeOptions,
            trackingName: 'visit-start-time-select',
          },
        },
        {
          type: 'input',
          name: 'duration',
          label: 'Duration',
          input: 'select',
          size: 1,
          disabled: !canEditEngagement,
          props: {
            options: visitDurations,
            trackingName: 'visit-duration-select',
          },
        },
      ],
    },
    {
      type: 'input',
      name: 'addressId',
      label: 'Location',
      input: 'select',
      props: {
        options: addressOptions,
        trackingName: 'visit-location-select',
      },
    },
    ...(addressId === -1
      ? [
          {
            type: 'input' as const,
            name: 'location' as const,
            label: 'Other Location',
            input: 'text' as const,
            disabled: !canEditEngagement,
            props: {
              trackingName: 'visit-other-location-input',
            },
          },
        ]
      : []),
    {
      type: 'input',
      name: 'outcomeId',
      label: 'Outcome',
      input: 'select',
      disabled: !canEditEngagement,
      props: {
        options: outcomeOptions,
        trackingName: 'visit-outcome-select',
      },
    },
    ...hospitalizationFollowUpFields,
    {
      type: 'input',
      name: 'note',
      label: 'Note',
      input: 'noteField',
      props: {
        ref: noteFieldRef,
        mode: noteMode,
        itemType: 'visit',
        itemId: visitId ? String(visitId) : undefined,
        noteId,
        userId,
        onRecoveredNoteChange,
        save: noteSave,
        draftType: 'visits',
        ...(noteMode === 'create'
          ? { draftId: MANUAL_VISIT_NOTE_DRAFT_ID }
          : {}),
        showCharacterCount: true,
        characterMinimum,
        trackingName: 'visit-note-input',
      },
    },
  ];
}
